<div class="page-content">
  <!-- Header Section -->
  <div class="page-title-container">
    <div class="breadcrumb-spacer"></div>
    <h1 class="page-title">Audit History</h1>
  </div>

  <div class="audit-container">
    <!-- Filters Section -->
    <mat-card class="filters-card">
      <mat-card-content>
        <form [formGroup]="filterForm" class="filters-form">
          <div class="filter-row">
            <!-- Search Field -->
            <mat-form-field appearance="outline" class="search-field">
              <mat-label>Search audit logs</mat-label>
              <input
                matInput
                [(ngModel)]="searchQuery"
                [ngModelOptions]="{ standalone: true }"
                placeholder="Search by message, entity ID..."
                autocomplete="off"
                (input)="onSearchInput()"
              />
              <mat-icon
                matSuffix
                [style.cursor]="searchQuery ? 'pointer' : 'default'"
                (click)="clearSearch()"
                >{{ searchQuery ? "close" : "search" }}</mat-icon
              >
            </mat-form-field>

            <!-- Entity Type Filter -->
            <mat-form-field appearance="outline" class="entity-field">
              <mat-label>Entity Type</mat-label>
              <mat-select formControlName="entityTypes" multiple>
                @for (type of entityTypes; track type.value) {
                  <mat-option [value]="type.value">
                    {{ type.name }}
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>

            <!-- Action Type Filter -->
            <mat-form-field appearance="outline" class="action-field">
              <mat-label>Action Type</mat-label>
              <mat-select formControlName="actionTypes" multiple>
                @for (type of auditLogTypes; track type.value) {
                  <mat-option [value]="type.value">
                    {{ type.name }}
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>

            <!-- Start Date/Time Filter -->
            <mat-form-field appearance="outline" class="datetime-field">
              <mat-label>Start Date/Time</mat-label>
              <input
                matInput
                type="datetime-local"
                formControlName="startDate"
                (change)="onDateChange()"
              />
            </mat-form-field>

            <!-- End Date/Time Filter -->
            <mat-form-field appearance="outline" class="datetime-field">
              <mat-label>End Date/Time</mat-label>
              <input
                matInput
                type="datetime-local"
                formControlName="endDate"
                (change)="onDateChange()"
              />
            </mat-form-field>

            <!-- Reset Button -->
            <button
              mat-stroked-button
              type="button"
              (click)="resetFilters()"
              class="reset-filters-btn"
              [disabled]="!hasFiltersChanged()"
            >
              <mat-icon>replay</mat-icon>
              Reset
            </button>
          </div>
        </form>
      </mat-card-content>
    </mat-card>

    <!-- Loading State -->
    @if (isLoading) {
      <div class="loading-container">
        <mat-spinner diameter="40"></mat-spinner>
        <span>Loading audit logs...</span>
      </div>
    }

    <!-- Error State -->
    @if (error && !isLoading) {
      <app-error-state
        [message]="error"
        (retry)="loadAuditLogs()"
      ></app-error-state>
    }

    <!-- Table Section -->
    @if (!isLoading && !error) {
      <mat-card class="table-card no-padding">
        <mat-card-content>
          <div class="table-container">
            <table
              mat-table
              [dataSource]="dataSource"
              matSort
              [matSortActive]="sortBy"
              [matSortDirection]="sortDirection"
              matSortDisableClear
              (matSortChange)="onSortChange($event)"
              class="audit-table"
            >
              <!-- Timestamp Column -->
              <ng-container matColumnDef="timestamp">
                <th mat-header-cell *matHeaderCellDef mat-sort-header>
                  Timestamp
                </th>
                <td mat-cell *matCellDef="let log">
                  <span class="timestamp">{{
                    formatTimestamp(log.timeStamp)
                  }}</span>
                </td>
              </ng-container>

              <!-- Action Column -->
              <ng-container matColumnDef="action">
                <th mat-header-cell *matHeaderCellDef mat-sort-header>
                  Action
                </th>
                <td mat-cell *matCellDef="let log">
                  <span
                    class="action-badge"
                    [ngClass]="getActionClass(log.auditLogTypeName)"
                  >
                    {{ log.auditLogTypeName }}
                  </span>
                </td>
              </ng-container>

              <!-- Entity Type Column -->
              <ng-container matColumnDef="entityType">
                <th mat-header-cell *matHeaderCellDef mat-sort-header>
                  Entity Type
                </th>
                <td mat-cell *matCellDef="let log">
                  <span class="entity-type">{{
                    log.databaseEntityTypeName
                  }}</span>
                </td>
              </ng-container>

              <!-- Entity ID Column -->
              <ng-container matColumnDef="entityId">
                <th mat-header-cell *matHeaderCellDef>Entity ID</th>
                <td mat-cell *matCellDef="let log">
                  <span class="entity-id">{{ log.entityId }}</span>
                </td>
              </ng-container>

              <!-- User Column -->
              <ng-container matColumnDef="user">
                <th mat-header-cell *matHeaderCellDef>User</th>
                <td mat-cell *matCellDef="let log">
                  <span class="user">{{
                    log.employee?.displayName || log.employeeId || "-"
                  }}</span>
                </td>
              </ng-container>

              <!-- Message Column -->
              <ng-container matColumnDef="message">
                <th mat-header-cell *matHeaderCellDef>Message</th>
                <td mat-cell *matCellDef="let log">
                  <span class="message">{{
                    truncateMessage(log.message, 80) || "-"
                  }}</span>
                </td>
              </ng-container>

              <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
              <tr
                mat-row
                *matRowDef="let row; columns: displayedColumns"
                class="clickable-row"
                (click)="openAuditDetail(row)"
              ></tr>

              <!-- Empty State -->
              <tr class="mat-row" *matNoDataRow>
                <td
                  class="mat-cell no-data-cell"
                  [attr.colspan]="displayedColumns.length"
                >
                  <div class="empty-state">
                    <mat-icon>search_off</mat-icon>
                    <span>No audit logs found matching your filters</span>
                  </div>
                </td>
              </tr>
            </table>
          </div>

          <mat-paginator
            [length]="totalCount"
            [pageSize]="pageSize"
            [pageIndex]="pageIndex"
            [pageSizeOptions]="pageSizeOptions"
            (page)="onPageChange($event)"
            showFirstLastButtons
          >
          </mat-paginator>
        </mat-card-content>
      </mat-card>
    }
  </div>
</div>
